import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useOnline } from "../offline.ts";
import { device } from "../opening.tsx";
import { ReportThis } from "../ReportThis.tsx";
import { Button } from "../souvenir.ts";
import { LAND } from "./common.ts";
import type { TripMapProps } from "./TripMap.tsx";

// The trip's map, its code loaded once a map is first shown (features/opening.md, "The map library loads with the
// map"): MapLibre is about half of what the app's scripts weigh, so a page without a map (Bookings, Your trips) opens
// without waiting for it. A page that opens on a map starts loading it as the app starts (main.tsx), alongside who
// I am and the trip. Until it's in, the map area is the map's own land, as the map is before anything is drawn on
// it; the service worker keeps it with the rest of the app (web/sw/sw.ts), so it opens with no signal once kept.

type Code = typeof import("./TripMap.tsx");
let code: Code | null = null;
let loading: Promise<Code> | null = null;

// The map's code, loaded once. A browser keeps a load that failed as failed for as long as the page is open, so a
// map whose code didn't load offers Reload (with signal: without, Reload would find nothing).
export function loadMap(): Promise<Code> {
  loading ??= import("./TripMap.tsx").then((loaded) => {
    code = loaded;
    return loaded;
  });
  return loading;
}

export function TripMap(props: TripMapProps) {
  const [map, setMap] = useState(() => code);
  const [failed, setFailed] = useState(false);
  const online = useOnline();
  const card = useRef<HTMLDivElement>(null);
  useAsWideAsItsWords(card, `${failed} ${online}`);
  useEffect(() => {
    if (map) return;
    let live = true;
    loadMap().then(
      (loaded) => live && setMap(loaded),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [map]);
  if (map) return <map.TripMap {...props} />;
  return (
    <div className="tm-map is-loading" style={{ background: LAND[props.theme] }} aria-busy={!failed}>
      {failed && (
        <div className="tm-map-failed" role="alert" ref={card}>
          {online ? (
            <>
              <p className="title">The map didn't load</p>
              <p className="body tm-muted">The signal may be too weak, or Tripmo was just updated.</p>
              <div className="tm-map-failed-actions">
                <Button variant="primary" size="sm" onClick={() => location.reload()}>
                  Reload
                </Button>
                <ReportThis line always />
              </div>
            </>
          ) : (
            <>
              <p className="title">No signal</p>
              <p className="body tm-muted">The map needs a connection the first time it opens on this {device()}.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// The card as wide as its words (DESIGN.md, section 5): its lines kept even (`text-wrap: balance`) end well short
// of the width they were balanced in, so the card, at most 360 px, is narrowed to its longest line, its title or
// its button, whichever is widest. Again whenever the map area changes size, and when what it says changes.
function useAsWideAsItsWords(ref: RefObject<HTMLElement | null>, says: string) {
  useLayoutEffect(() => {
    const el = ref.current;
    const area = el?.parentElement;
    if (!el || !area) return;
    // Measured again when what it says changes.
    void says;
    const narrow = () => {
      el.style.width = "";
      const box = el.getBoundingClientRect();
      const style = getComputedStyle(el);
      let right = box.left;
      for (const child of el.children) {
        if (child instanceof HTMLParagraphElement) {
          // Each line's own right, not the paragraph's box.
          const lines = document.createRange();
          lines.selectNodeContents(child);
          for (const line of lines.getClientRects()) right = Math.max(right, line.right);
        } else right = Math.max(right, child.getBoundingClientRect().right);
      }
      const width = right - box.left + parseFloat(style.paddingRight) + parseFloat(style.borderRightWidth);
      el.style.width = `${Math.ceil(width)}px`;
    };
    narrow();
    const watch = new ResizeObserver(narrow);
    watch.observe(area);
    return () => watch.disconnect();
  }, [ref, says]);
}
